import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ComplianceTrendWidget = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/rtbf/compliance-trend?months=12`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData({ series: [], total_executed: 0, average_on_time_pct: null }));
  }, []);

  if (!data) {
    return (
      <Card data-testid="compliance-trend-widget" className="border-slate-200">
        <CardContent className="p-8 text-center text-slate-500">A carregar tendência...</CardContent>
      </Card>
    );
  }

  const avg = data.average_on_time_pct;
  const avgLabel = avg === null ? '—' : `${avg}%`;
  const avgColor = avg === null ? 'text-slate-400' : avg >= 95 ? 'text-emerald-600' : avg >= 80 ? 'text-amber-600' : 'text-red-600';

  return (
    <Card data-testid="compliance-trend-widget" className="border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Taxa de cumprimento — últimos 12 meses
            </CardTitle>
            <CardDescription>
              % de pedidos executados dentro do prazo legal (Art. 12.º(3) RGPD)
            </CardDescription>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${avgColor}`} data-testid="compliance-trend-average">{avgLabel}</div>
            <p className="text-xs text-slate-500">
              {data.total_on_time}/{data.total_executed} pedidos
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.series.length === 0 || data.total_executed === 0 ? (
          <p className="text-center py-10 text-slate-500">
            Ainda sem histórico suficiente para desenhar a tendência.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.series} margin={{ top: 12, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                label={{ value: 'Pedidos', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 11 }}
                unit="%"
              />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value, name) => (name === 'Taxa cumprimento' ? [`${value ?? '—'}%`, name] : [value, name])}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="on_time" name="No prazo" stackId="a" fill="#10B981" />
              <Bar yAxisId="left" dataKey="late" name="Fora de prazo" stackId="a" fill="#EF4444" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="on_time_pct"
                name="Taxa cumprimento"
                stroke="#0F172A"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplianceTrendWidget;
